/** Header search: watchlist hits navigate in place, CoinGecko hits can be added to the watchlist. */
import { api } from "@client/lib/api";
import { invalidateAfterTokenChange, searchQuery } from "@client/lib/queries";
import { useToast } from "@client/lib/toast";
import { price } from "@shared/format";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import type { KeyboardEvent, RefObject } from "react";
import { useEffect, useRef, useState } from "react";

type SearchBoxProps = {
  readonly inputRef: RefObject<HTMLInputElement | null>;
};

export function SearchBox({ inputRef }: SearchBoxProps) {
  const [text, setText] = useState("");
  const [debounced, setDebounced] = useState("");
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(text.trim()), 250);
    return () => window.clearTimeout(t);
  }, [text]);

  useEffect(() => {
    setOpen(text.trim().length > 0);
  }, [text]);

  useEffect(() => {
    function onDocMouseDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, []);

  const results = useQuery(searchQuery(debounced));

  const addMutation = useMutation({
    mutationFn: (cgId: string) => api.tokens.add(cgId),
    onSuccess: async (newToken) => {
      await invalidateAfterTokenChange(qc);
      setOpen(false);
      setText("");
      await navigate({ to: "/watchlista", search: { sel: newToken.id } });
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "Nie udało się dodać tokena."),
  });

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      setOpen(false);
      e.currentTarget.blur();
    }
  };

  const selectOwn = async (tokenId: string) => {
    setOpen(false);
    setText("");
    await navigate({ to: "/watchlista", search: { sel: tokenId } });
  };

  const own = results.data?.own ?? [];
  const coingecko = results.data?.coingecko ?? [];
  const noHits = debounced.length > 0 && own.length === 0 && coingecko.length === 0 && !results.isFetching;

  return (
    <div ref={containerRef} className="relative min-w-0 flex-1 md:min-w-[200px] md:max-w-[380px]">
      <svg
        width={16}
        height={16}
        viewBox="0 0 24 24"
        fill="none"
        stroke="var(--color-fg-3)"
        strokeWidth="2"
        strokeLinecap="round"
        className="pointer-events-none absolute left-3 top-[11px]"
      >
        <circle cx="11" cy="11" r="7" />
        <path d="m20 20-3.5-3.5" />
      </svg>
      <input
        ref={inputRef}
        data-search-input
        value={text}
        onChange={(e) => setText(e.target.value)}
        onFocus={() => text.trim().length > 0 && setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder="Szukaj tokena…"
        className="h-[38px] w-full rounded-md border border-line bg-app pl-9 pr-3 text-sm text-fg-1 placeholder:text-fg-4 md:pr-10"
      />
      <span className="mono pointer-events-none absolute right-2.5 top-[9px] hidden rounded border border-line-2 px-1.5 text-[11px] text-fg-3 md:block">
        /
      </span>
      {open && (
        <div className="fixed inset-x-3 top-[62px] z-30 max-h-[70vh] overflow-y-auto overflow-x-hidden rounded-md border border-line-2 bg-surface shadow-md md:absolute md:inset-x-auto md:left-0 md:top-[44px] md:w-[max(100%,420px)]">
          {own.length > 0 && (
            <>
              <div className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-fg-3">
                Na watchliście
              </div>
              {own.map((token) => (
                <button
                  key={token.id}
                  type="button"
                  onClick={() => selectOwn(token.id)}
                  className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-[13px] text-fg-1 hover:bg-line"
                >
                  <span className="w-14 shrink-0 font-semibold">{token.symbol}</span>
                  <span className="min-w-0 flex-1 truncate text-fg-3">{token.name}</span>
                  <span className="mono shrink-0 whitespace-nowrap">{price(token.priceUsd)}</span>
                </button>
              ))}
            </>
          )}
          <div className="border-t border-line px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-fg-3">
            CoinGecko · search
          </div>
          {coingecko.map((hit) => (
            <div key={hit.cgId} className="flex items-center gap-2.5 px-3 py-1.5 text-[13px] hover:bg-line">
              <span className="w-14 shrink-0 font-semibold">{hit.symbol}</span>
              <span className="min-w-0 flex-1 truncate text-fg-3">
                {hit.name} {hit.rank != null && <span className="text-[11px]">· #{hit.rank}</span>}
              </span>
              <span className="mono shrink-0 whitespace-nowrap">{price(hit.priceUsd)}</span>
              <button
                type="button"
                disabled={addMutation.isPending}
                onClick={() => addMutation.mutate(hit.cgId)}
                className="h-[26px] shrink-0 whitespace-nowrap rounded-md border border-transparent bg-accent px-2.5 text-xs font-medium text-white hover:bg-accent-hover disabled:opacity-60"
              >
                + Dodaj
              </button>
            </div>
          ))}
          {noHits && <div className="px-3 py-2.5 text-xs text-fg-3">Brak wyników dla „{debounced}”.</div>}
        </div>
      )}
    </div>
  );
}
